/**
 * Cookie-based Storage Adapter for Supabase Auth
 *
 * iOS Safari's Intelligent Tracking Prevention (ITP) clears localStorage
 * after 7 days of inactivity. This adapter uses cookies instead, which
 * persist properly on iOS browsers.
 *
 * Used conditionally for mobile browsers only - desktop continues using localStorage.
 */

import Cookies from 'js-cookie';

// Cookie configuration for auth tokens
const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 7, // 7 days
  secure: true, // HTTPS only
  sameSite: 'lax', // CSRF protection while allowing navigation
  path: '/',
  // Use root domain for subdomains, but not on localhost
  domain: typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? '.dextoro.com'
    : undefined,
};

/**
 * Cookie-based storage adapter implementing Supabase's storage interface
 */
export const cookieStorage = {
  getItem: (key: string): string | null => {
    try {
      return Cookies.get(key) || null;
    } catch (error) {
      console.warn('[CookieStorage] Failed to get item:', error);
      return null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      Cookies.set(key, value, COOKIE_OPTIONS);
    } catch (error) {
      console.warn('[CookieStorage] Failed to set item:', error);
    }
  },

  removeItem: (key: string): void => {
    try {
      // Remove from both the specific domain and root
      Cookies.remove(key, { path: '/' });
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        Cookies.remove(key, { path: '/', domain: '.dextoro.com' });
      }
    } catch (error) {
      console.warn('[CookieStorage] Failed to remove item:', error);
    }
  },
};

/**
 * Mobile/iOS detection helpers
 */

export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
};

export const isMobileSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
};

export const isIOSChrome = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && /CriOS/.test(navigator.userAgent);
};

export const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

export const isInAppBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Detect wallet in-app browsers and social app browsers
  return /FBAN|FBAV|Instagram|Twitter|Phantom|Solflare|TrustWallet/.test(ua);
};

/**
 * Determines if cookie storage should be used
 * Returns true for iOS and Android mobile browsers
 */
export const shouldUseCookieStorage = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isIOS() || isAndroid() || isInAppBrowser();
};

/**
 * Test if cookies are available (not in private browsing mode)
 */
export const areCookiesAvailable = (): boolean => {
  if (typeof document === 'undefined') return false;
  try {
    const testKey = '__dextoro_cookie_test__';
    Cookies.set(testKey, 'test', { expires: 1 });
    const testValue = Cookies.get(testKey);
    Cookies.remove(testKey);
    return testValue === 'test';
  } catch {
    return false;
  }
};

/**
 * Get the appropriate storage for the current environment
 * Falls back to localStorage if cookies are unavailable (e.g., private browsing)
 */
export const getStorage = (): Storage | typeof cookieStorage => {
  if (typeof window === 'undefined') {
    // SSR context - return a no-op storage
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    } as Storage;
  }

  if (shouldUseCookieStorage()) {
    // Check if cookies actually work (private browsing blocks them)
    if (areCookiesAvailable()) {
      return cookieStorage;
    }
    console.warn('[CookieStorage] Cookies unavailable (private browsing?), falling back to localStorage');
  }

  return window.localStorage;
};
